import isNil from 'lodash/isNil'
import {
  FILE_UPLOAD_QUESTION_EXTENSIONS,
  SUMMARY_DEFAULT_TEMPLATE,
} from 'utils/constants'
import { v4 as uuid } from 'uuid'

import QuestionChoice from './QuestionChoice'
import QuestionColumn from './QuestionColumn'
import QuestionRow from './QuestionRow'

let questionOrder = 0

export function getRandomDigitString(length = 8) {
  let result = ''

  for (let i = 0; i < length; i++) {
    result += Math.floor(Math.random() * 9) + 1
  }
  return result
}

class FormBuilderQuestion {
  constructor(
    {
      conditional_rule_groups,
      demographics,
      display_columns,
      athena_integration,
      chirotouch_integration,
      elation_integration,
      open_dental_integration,
      form_index,
      formula,
      help_text,
      id,
      id_for_builder,
      image,
      imageToDisplayOnBuilder,
      is_office_use_only,
      is_read_only,
      is_required,
      is_visible,
      num_columns,
      num_scale_max,
      num_scale_min,
      order,
      placeholder,
      question_choices,
      question_columns,
      question_rows,
      question_type,
      row,
      section,
      text,
      calculation_value_type,
    },
    isNew = true,
    hasChanged = false
  ) {
    this.section = section ?? uuid()
    this.row = row ?? uuid()
    this.id = id ?? parseInt(Math.random() * 1000000000000000).toString()
    this.original_id = this.id
    if (!isNil(display_columns) && Number.isInteger(Number(display_columns))) {
      this.display_columns = Number.parseInt(display_columns)
    } else {
      this.display_columns = 6
    }
    this.help_text = help_text ?? ''
    this.placeholder = placeholder ?? ''
    this.image = image ?? null
    this.imageToDisplayOnBuilder = imageToDisplayOnBuilder ?? null
    this.is_office_use_only = is_office_use_only ?? false
    this.calculation_value_type = calculation_value_type ?? undefined
    this.is_required = is_required ?? false
    this.is_visible = is_visible ?? true
    this.num_columns = num_columns ?? 1
    this.num_scale_max = num_scale_max ?? null
    this.num_scale_min = num_scale_min ?? null
    this.order = order ?? questionOrder++
    this.question_choices = question_choices ?? []
    this.question_columns = question_columns ?? []
    this.question_rows = question_rows ?? []
    this.question_type = question_type ?? {
      question_type: 'text',
      question_subtype: 'single_line',
    }
    this.athena_integration = athena_integration ?? null
    this.chirotouch_integration = chirotouch_integration ?? null
    this.elation_integration = elation_integration ?? null
    this.open_dental_integration = open_dental_integration ?? null
    this.conditional_rule_groups = conditional_rule_groups ?? []
    this.id_for_builder = id_for_builder ?? question_type?.question_type
    this.text = text
    this._assignDefaultValues()
    this.isNew = isNew
    this.hasChanged = hasChanged
    this.demographics = demographics
    this.is_read_only = is_read_only
    this.formula = formula
    this.form_index = form_index != null ? form_index : getRandomDigitString()
    this.inverse_matrix = true
    this.include_completed_date = false
    this.multi_file_upload = false

    if (this.question_type?.question_subtype === 'calculation') {
      this.formula = this.formula || ''
    }

    if (this.question_type?.question_type === 'summary') {
      this.hidden = true
      if (this.text === 'Untitled') {
        this.text = SUMMARY_DEFAULT_TEMPLATE
      }
    }
  }

  _assignDefaultText(text = 'Untitled') {
    if (!this.text) {
      this.text = text
    }
  }

  _assignDefaultChoices() {
    if (this.question_choices.length === 0) {
      this.question_choices = Array(3)
        .fill()
        .map(
          (x, i) =>
            new QuestionChoice({
              order: i,
              question: this.id,
              text_value: `Choice ${i + 1}`,
            })
        )
    }
  }

  _assignDefaultColumns() {
    if (this.question_columns.length === 0) {
      this.question_columns = Array(3)
        .fill()
        .map(
          (x, i) =>
            new QuestionColumn({
              order: i,
              question: this.id,
              text_value: `Column ${i + 1}`,
            })
        )
    }
  }

  _assignDefaultRows(rows = 3) {
    if (this.question_rows.length === 0) {
      this.question_rows = Array(rows)
        .fill()
        .map(
          (x, i) =>
            new QuestionRow({
              order: i,
              question: this.id,
              text: `Row ${i + 1}`,
            })
        )
    }
  }

  _assignDefaultFileExtensions() {
    if (!this.file_extensions) {
      this.file_extensions = FILE_UPLOAD_QUESTION_EXTENSIONS
    }
  }

  _assignDefaultValues() {
    const { question_type } = this.question_type

    switch (question_type) {
      case 'display_only':
        this._assignDefaultText('Add and format text, links, and images here.')
        break
      case 'radio':
      case 'checkbox':
        this._assignDefaultText()
        this._assignDefaultChoices()
        break
      case 'matrix':
        this._assignDefaultText()
        this._assignDefaultColumns()
        this._assignDefaultRows()
        break
      case 'table':
        this._assignDefaultText()
        this._assignDefaultColumns()
        this._assignDefaultRows(1)
        break
      case 'file_upload':
        this._assignDefaultFileExtensions()
        this._assignDefaultText()
        break
      default:
        this._assignDefaultText()
    }
  }
}

export default FormBuilderQuestion
